import type {
  AutonomyLevelDto,
  ContentKind,
  ContentReviewDto,
  ContentStatus,
  EditorialCriterionId,
  EditorialMediaCoverage,
  EditorialMediaCriterionId,
  EditorialMediaUnavailableReason,
  EditorialRecommendation,
  EditorialUnavailableReason,
  GateOutcome,
  HealthStatus,
  MapaCapabilityName,
  MapaCapabilityReason,
  MapaConversationDto,
  MapaStageId,
  MetricValueDto,
  MoneyMetricDto,
  QualityGateId,
  SourcedMetricDto,
} from '@devmil/contracts';

/**
 * Fuso do painel. Fixo no código, e não herdado da máquina, para o rótulo de
 * horário ser o mesmo no teste, no CI e na tela do Thiago.
 */
export const PANEL_TIME_ZONE = 'America/Sao_Paulo';

const TIME_FORMAT = new Intl.DateTimeFormat('pt-BR', {
  timeZone: PANEL_TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/** `2026-08-27T17:30:00Z` vira `14:30`. */
export const formatTimeOfDay = (isoInstant: string): string =>
  TIME_FORMAT.format(new Date(isoInstant));

const DATE_TIME_FORMAT = new Intl.DateTimeFormat('pt-BR', {
  timeZone: PANEL_TIME_ZONE,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/**
 * `2026-08-27T17:30:00Z` vira `27/08/2026 14:30`.
 *
 * Prazo legal e prova do titular precisam do dia, não só da hora: `14:30` sem
 * data não distingue hoje de anteontem, e é exatamente essa diferença que
 * decide se um prazo da LGPD venceu.
 */
export const formatDateTime = (isoInstant: string): string =>
  DATE_TIME_FORMAT.format(new Date(isoInstant));

export const KIND_LABEL: Record<ContentKind, string> = {
  reel: 'Reel',
  story: 'Story',
  // "Foto", e nao "Post" nem "Imagem": o dono precisa distinguir a especie da
  // peca em quatro caracteres na lateral do cartao, e as tres publicam no
  // Instagram. O que separa esta das outras duas e ser foto parada no feed.
  photo: 'Foto',
  // "Carrossel", por extenso, e nao "Carr." nem "7 slides": o rotulo diz a
  // ESPECIE, e a quantidade de slides muda de peca para peca no dia em que o
  // formato ganhar variacao. Quantos slides tem esta peca a tela mostra no
  // navegador da sequencia, medido, e nao no rotulo.
  carousel: 'Carrossel',
};

const WEEKDAY_FORMAT = new Intl.DateTimeFormat('pt-BR', {
  timeZone: PANEL_TIME_ZONE,
  weekday: 'short',
});

const DAY_MONTH_FORMAT = new Intl.DateTimeFormat('pt-BR', {
  timeZone: PANEL_TIME_ZONE,
  day: '2-digit',
  month: '2-digit',
});

/**
 * `2026-09-04` mais um dia vira `2026-09-05`, sem passar perto de fuso.
 *
 * A conta acontece toda em UTC, ancorada ao meio-dia: somar 24 horas a partir
 * da meia-noite quebraria em qualquer país com horário de verão, e ancorar no
 * meio-dia deixa a folga de doze horas que nenhum deslocamento consome.
 */
const diaSeguinte = (localDate: string): string =>
  new Date(new Date(`${localDate}T12:00:00.000Z`).getTime() + 86_400_000)
    .toISOString()
    .slice(0, 10);

/**
 * O rótulo do separador de dia na fila: `Hoje, 03/09`, `Amanhã, 04/09`,
 * `Sex, 05/09`.
 *
 * `operationDate` é o dia da operação que o snapshot já traz, e não
 * `new Date()`: às 23:30 de São Paulo o relógio do navegador e o do servidor
 * concordam, mas o do contêiner em UTC já virou. Perguntar duas vezes que dia
 * é dá duas respostas, e a fila e o título ficariam contando dias diferentes.
 *
 * O meio-dia UTC no meio da conta não é enfeite: `2026-09-04T00:00:00Z` lido
 * em São Paulo é 3 de setembro, e o rótulo sairia um dia atrás.
 */
export const formatQueueDay = (
  localDate: string,
  operationDate: string,
): string => {
  const dia = new Date(`${localDate}T12:00:00.000Z`);
  const numero = DAY_MONTH_FORMAT.format(dia);
  if (localDate === operationDate) {
    return `Hoje, ${numero}`;
  }
  if (localDate === diaSeguinte(operationDate)) {
    return `Amanhã, ${numero}`;
  }
  // `pt-BR` devolve `sex.`; a tela quer `Sex`.
  const semana = WEEKDAY_FORMAT.format(dia).replace(/\.$/u, '');
  return `${semana.charAt(0).toUpperCase()}${semana.slice(1)}, ${numero}`;
};

export const GATE_LABEL: Record<QualityGateId, string> = {
  source_and_proof: 'Fonte e tipo de prova',
  claims_and_numbers: 'Afirmações e números',
  confidentiality: 'Sigilo e identificação',
  originality: 'Originalidade',
  brand_and_readability: 'Marca e leitura',
  media_rights: 'Direitos de mídia',
  cta_and_destination: 'CTA e destino',
};

export const GATE_OUTCOME_LABEL: Record<GateOutcome, string> = {
  pass: 'PASSA',
  alert: 'ALERTA',
  blocked: 'BLOQUEIA',
};

export const GATE_OUTCOME_CLASS: Record<GateOutcome, string> = {
  pass: 'dm-gate-pass',
  alert: 'dm-gate-alert',
  blocked: 'dm-gate-blocked',
};

/**
 * As cinco dimensões da régua editorial, na letra da tela.
 *
 * Os rótulos são frases curtas, e não os nomes técnicos do contrato: quem lê
 * esta tela decide sobre uma peça, e `promise_coherence` não diz nada a
 * ninguém às sete da manhã.
 */
export const EDITORIAL_CRITERION_LABEL: Record<EditorialCriterionId, string> = {
  audience_relevance: 'Fala com este público',
  hook_clarity: 'Gancho claro',
  practical_delivery: 'Entrega prática',
  promise_coherence: 'Cumpre a promessa',
  format_fit: 'Cabe no formato',
};

/**
 * A recomendação do editor, e o cuidado com a palavra.
 *
 * `blocked` NÃO vira "bloqueada": quem bloqueia peça são os sete portões, e usar
 * a mesma palavra faria o dono ler o parecer como veredito. O editor diz que não
 * publicaria, e a peça continua no caminho normal de aprovação.
 */
export const EDITORIAL_RECOMMENDATION_LABEL: Record<
  EditorialRecommendation,
  string
> = {
  fit: 'Apto editorialmente',
  rewrite: 'Reescreveria',
  blocked: 'Não publicaria',
};

export const EDITORIAL_RECOMMENDATION_CLASS: Record<
  EditorialRecommendation,
  string
> = {
  fit: 'dm-gate-pass',
  rewrite: 'dm-gate-alert',
  blocked: 'dm-gate-blocked',
};

/**
 * Por que NÃO há parecer, escrito para quem aprova.
 *
 * Cada motivo pede uma reação diferente, e por isso nenhum deles é "sem
 * parecer": desligado é decisão do dono, teto atingido é gasto do dia, falha é
 * investigação, e texto mudado é a nota velha sendo escondida de propósito.
 */
export const EDITORIAL_UNAVAILABLE_LABEL: Record<
  EditorialUnavailableReason,
  string
> = {
  OFF: 'Revisão editorial desligada. Nenhuma peça foi avaliada.',
  STALE: 'O texto mudou depois da avaliação. Não há parecer deste texto.',
  // A régua, e não o texto. Sem esta linha própria, a mensagem de `STALE`
  // mandava procurar uma edição que nunca houve: quem investigasse compararia
  // duas versões idênticas do mesmo roteiro.
  HASH_VERSION:
    'A régua de identidade do texto mudou depois da avaliação. O parecer antigo não vale mais para este texto.',
  // Os dois motivos de orçamento, separados desde 2026-09-06. Um é gasto
  // previsto e pede olhar o dia; o outro é defeito e pede investigação.
  BUDGET_EXHAUSTED: 'O teto de chamadas do dia foi atingido antes desta peça.',
  BUDGET_UNRECORDED:
    'O registro do gasto não pôde ser gravado, então nada foi chamado. Isso é falha de registro, e não teto atingido.',
  FAILED: 'A avaliação falhou. A peça segue para a sua revisão.',
  NOT_REVIEWED: 'Esta peça não passou por revisão editorial.',
};

/**
 * As três dimensões da régua VISUAL, na letra da tela.
 *
 * Frases curtas, como as do texto, e pelo mesmo motivo: `silent_comprehension`
 * não diz nada a ninguém às sete da manhã.
 */
export const EDITORIAL_MEDIA_CRITERION_LABEL: Record<
  EditorialMediaCriterionId,
  string
> = {
  silent_comprehension: 'Entende no mudo',
  reading_comfort: 'Dá tempo de ler',
  story_coherence: 'Imagem e texto combinam',
};

/**
 * Por que NÃO há parecer da MÍDIA, escrito para quem aprova.
 *
 * Mapa próprio, e não o do texto reaproveitado: `NO_STILL` só existe aqui, e
 * "não passou por revisão" significa coisas diferentes quando o que falta é a
 * leitura da imagem.
 */
export const EDITORIAL_MEDIA_UNAVAILABLE_LABEL: Record<
  EditorialMediaUnavailableReason,
  string
> = {
  OFF: 'Revisão da mídia desligada. Nenhuma imagem desta peça foi avaliada.',
  STALE:
    'A mídia ou o texto mudaram depois da avaliação. Não há parecer desta mídia.',
  HASH_VERSION:
    'A régua de identidade mudou depois da avaliação. O parecer antigo não vale mais para esta peça.',
  BUDGET_EXHAUSTED: 'O teto de chamadas do dia foi atingido antes desta peça.',
  BUDGET_UNRECORDED:
    'O registro do gasto não pôde ser gravado, então nada foi chamado. Isso é falha de registro, e não teto atingido.',
  FAILED: 'A avaliação da mídia falhou. A peça segue para a sua revisão.',
  NOT_REVIEWED: 'A mídia desta peça não passou por revisão.',
  // O estado que a auditoria de 06/09/2026 mediu na Story, e que ficou raro
  // depois que o renderizador passou a gravar uma amostra por tela.
  NO_STILL:
    'Esta peça não tem imagem parada para olhar: nem capa, nem foto, nem amostra de tela.',
};

/**
 * O QUE o crítico olhou, dito em uma frase.
 *
 * É a linha mais importante do bloco visual, e o motivo é o achado F11: um
 * parecer favorável sobre a CAPA, lido como parecer sobre a peça, é uma nota
 * favorável que ninguém deu. A frase da capa diz "capa" com todas as letras, e
 * a das amostras diz quantas telas de quantas.
 *
 * Nenhuma delas afirma que o vídeo foi assistido, e nenhuma pode: no contrato,
 * `videoReviewed` é `false` literal.
 */
export const editorialMediaCoverageLabel = (
  coverage: EditorialMediaCoverage,
): string => {
  switch (coverage.kind) {
    case 'photo':
      return 'Olhou a foto publicada, que é a peça inteira.';
    case 'cover':
      return coverage.screens === null
        ? 'Olhou só a CAPA. O vídeo não foi revisado.'
        : `Olhou só a CAPA, de ${coverage.screens} telas. O vídeo não foi revisado.`;
    case 'samples':
      return (
        `Olhou ${coverage.stills} de ${coverage.screens} telas, em imagem parada ` +
        `(${Math.round(coverage.coveredMs / 1000)} s de ${Math.round(
          coverage.totalMs / 1000,
        )} s). O vídeo não foi assistido.`
      );
    case 'carousel':
      /*
       * A frase do carrossel diz DOIS números e nenhum tempo.
       *
       * "Olhou 1 de 7 slides" é a linha que impede um parecer sobre a capa de
       * ser lido como parecer sobre a sequência, que é onde mora metade do
       * julgamento de um carrossel: se o slide 4 repete o 2, quem viu só o 1
       * não tem como saber.
       *
       * Quando os sete foram abertos, a frase muda de tom em vez de sumir: a
       * cobertura passa a ser integral, e dizer isso é tão importante quanto
       * dizer que ela é parcial.
       */
      return coverage.stills >= coverage.slides
        ? `Olhou os ${coverage.slides} slides publicados, na ordem.`
        : `Olhou ${coverage.stills} de ${coverage.slides} slides. A sequência inteira não foi revisada.`;
  }
};

/**
 * O que aconteceu com a peça DEPOIS da liberação.
 *
 * Cinco estados, e nenhum deles é "gerada". Era esse o defeito do achado F12:
 * a tela contava peça gerada e escrevia "meta diária completa" embaixo, e peça
 * gerada não é peça no ar.
 */
export const PUBLICATION_LABEL: Record<
  ContentReviewDto['publication']['state'],
  string
> = {
  pending_release: 'Aguardando decisão',
  released: 'Liberada',
  scheduled: 'Agendada',
  published: 'Publicada',
  expired: 'Vencida',
  failed: 'Falhou ao publicar',
};

export interface QueueState {
  label: string;
  className: string;
}

/**
 * O selo da fila.
 *
 * O vocabulário é curto e concorda em gênero com "peça", que é o que a linha
 * mostra: `APROVADA`, `REJEITADA`, `BLOQUEADA`, `AJUSTES`, `AGUARDANDO`.
 *
 * `ALERTA` e `BLOQUEIA` NÃO são status, e continuam aqui de propósito: são a
 * peça que ainda aguarda decisão e tem portão em alerta ou em bloqueio, e o
 * revisor precisa ver a diferença antes de clicar. Reduzir os dois a
 * `AGUARDANDO` deixaria o selo curto e cego — a faixa de portões é a última
 * defesa do dono no nível 0 de autonomia, e a fila é onde ele escolhe qual
 * peça abrir primeiro. `BLOQUEIA` é o verbo (o portão bloqueia esta peça que
 * espera decisão) e `BLOQUEADA` é o estado (a esteira já a barrou).
 */
export const queueStateOf = (piece: ContentReviewDto): QueueState => {
  /*
   * A peça LIBERADA passa a mostrar o que aconteceu com ela depois.
   *
   * Até 2026-09-06 toda peça aprovada mostrava `APROVADA` até o fim do dia, e
   * o selo não mudava nem quando a publicação saía, nem quando ela falhava
   * (achado F12). O dono via a mesma palavra numa peça no ar e numa peça cujo
   * horário passou em branco.
   *
   * `APROVADA` continua existindo, e agora significa exatamente o que diz: o
   * dono liberou e a fila de publicação ainda não pegou a peça.
   */
  if (piece.status === 'approved' || piece.status === 'published') {
    switch (piece.publication.state) {
      case 'published':
        return {label: 'PUBLICADA', className: 'dm-state is-approved'};
      case 'scheduled':
        return {label: 'AGENDADA', className: 'dm-state is-approved'};
      case 'failed':
        return {label: 'FALHOU', className: 'dm-state is-rejected'};
      case 'expired':
        return {label: 'VENCIDA', className: 'dm-state is-rejected'};
      default:
        return {label: 'APROVADA', className: 'dm-state is-approved'};
    }
  }
  const byStatus: Partial<Record<ContentStatus, QueueState>> = {
    change_requested: {label: 'AJUSTES', className: 'dm-state'},
    rejected: {label: 'REJEITADA', className: 'dm-state is-rejected'},
    blocked: {label: 'BLOQUEADA', className: 'dm-state is-rejected'},
  };
  const known = byStatus[piece.status];
  if (known) {
    return known;
  }
  const worst = piece.gateResults.some((gate) => gate.outcome === 'blocked')
    ? 'BLOQUEIA'
    : piece.gateResults.some((gate) => gate.outcome === 'alert')
      ? 'ALERTA'
      : 'AGUARDANDO';
  return {label: worst, className: 'dm-state'};
};

// ---------------------------------------------------------------------------
// MAPA
// ---------------------------------------------------------------------------

export const MAPA_STAGE_LABEL: Record<MapaStageId, string> = {
  started: 'MAPA iniciado',
  diagnosed: 'Diagnóstico concluído',
  whatsapp: 'WhatsApp consentido',
  meeting: 'Reunião registrada',
};

const MAPA_CHANNEL_LABEL: Record<
  MapaConversationDto['origin']['channel'],
  string
> = {
  reel: 'Reel',
  story: 'Story',
  instagram: 'Instagram',
  /*
   * `unknown` entrou em 07/09/2026, com o passo 5 do lote 09.
   *
   * Ate aqui toda conversa medida saia com `instagram`, escrito a mao no
   * montador do snapshot: uma afirmacao de canal para conversas cuja origem
   * ninguem tinha lido. "Desconhecida" e a unica celula honesta quando nenhum
   * `referral` chegou, e ela e diferente de "Instagram", que agora significa
   * "veio origem e o canal especifico nao esta na lista de permissao".
   */
  unknown: 'Desconhecida',
};

/**
 * As oito capacidades do funil, no rotulo que a tela mostra.
 *
 * Nomes de PERCURSO, e nao nomes de modulo: quem le o painel quer saber o que
 * acontece com quem escreveu, e nao qual arquivo esta injetado.
 */
export const MAPA_CAPABILITY_LABEL: Record<MapaCapabilityName, string> = {
  webhook_ingress: 'Recebimento do webhook',
  transicao: 'Maquina do diagnostico',
  aviso_de_privacidade: 'Aviso de privacidade ativo',
  enfileiramento_de_envio: 'Enfileiramento da resposta',
  envio_de_mensagem: 'Envio da mensagem',
  link_de_whatsapp: 'Emissao do link do WhatsApp',
  resgate_manual: 'Resgate do codigo',
  redirecionador_de_campanha: 'Link de campanha da bio',
};

/** O motivo, na mesma caixa alta curta que o resto dos valores da tela usa. */
export const MAPA_CAPABILITY_REASON_LABEL: Record<
  MapaCapabilityReason,
  string
> = {
  ATIVA: 'ATIVA',
  NAO_CONFIGURADA: 'NAO CONFIGURADA',
  NAO_LIGADA: 'NAO LIGADA',
  NAO_IMPLEMENTADA: 'NAO IMPLEMENTADA',
  SEM_APROVACAO_DO_DONO: 'AGUARDA APROVACAO',
};

/**
 * A cor de cada motivo, e a diferenca entre amarelo e vermelho importa.
 *
 * Amarelo e o que uma decisao resolve: falta variavel de ambiente, ou falta
 * injetar uma porta que ja existe. Vermelho e codigo que nao existe, e nenhuma
 * variavel liga. Pintar os dois igual faria o dono procurar interruptor para
 * uma coisa que precisa ser escrita.
 */
export const MAPA_CAPABILITY_CLASS: Record<MapaCapabilityReason, string> = {
  ATIVA: 'dm-ok',
  NAO_CONFIGURADA: 'dm-ok is-degraded',
  NAO_LIGADA: 'dm-ok is-degraded',
  NAO_IMPLEMENTADA: 'dm-ok is-failed',
  // Amarelo, e nao vermelho: falta uma decisao humana, e nao codigo.
  SEM_APROVACAO_DO_DONO: 'dm-ok is-degraded',
};

export const MAPA_NEXT_ACTION_LABEL: Record<
  MapaConversationDto['nextAction'],
  string
> = {
  await_whatsapp: 'Aguardar WhatsApp',
  redeem_code: 'Resgatar código',
  await_reply: 'Aguardar resposta',
  commercial_event: 'Evento comercial',
};

/**
 * Coluna `Etapa`: com diagnóstico em curso a tela mostra o progresso, que diz
 * mais ao operador do que o nome da etapa.
 */
export const mapaStageCell = (conversation: MapaConversationDto): string =>
  conversation.diagnostic
    ? `Diagnóstico ${conversation.diagnostic.answered}/${conversation.diagnostic.total}`
    : MAPA_STAGE_LABEL[conversation.stage];

export const mapaOriginCell = (conversation: MapaConversationDto): string => {
  const channel = MAPA_CHANNEL_LABEL[conversation.origin.channel];
  return conversation.origin.label
    ? `${channel} · ${conversation.origin.label}`
    : channel;
};

export const mapaWindowCell = (conversation: MapaConversationDto): string => {
  switch (conversation.window.kind) {
    case 'countdown':
      return `${conversation.window.hoursRemaining}h restantes`;
    case 'associated':
      return 'Associada';
    case 'completed':
      return 'Concluída';
  }
};

// ---------------------------------------------------------------------------
// Métricas e operação
// ---------------------------------------------------------------------------

/** O que a tela mostra quando o número não foi medido. Nunca zero. */
export const UNAVAILABLE = 'Indisp.';

const COMPACT = new Intl.NumberFormat('pt-BR', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

const PLAIN = new Intl.NumberFormat('pt-BR', {maximumFractionDigits: 0});

const TWO_DECIMALS = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Acima de mil o painel compacta: `18400` vira `18,4 mil`. */
export const formatMetricValue = (metric: MetricValueDto): string => {
  if (metric.availability === 'unavailable') {
    return UNAVAILABLE;
  }
  return metric.value >= 1000
    ? COMPACT.format(metric.value)
    : PLAIN.format(metric.value);
};

export const formatPercent = (metric: MetricValueDto): string =>
  metric.availability === 'unavailable'
    ? UNAVAILABLE
    : `${TWO_DECIMALS.format(metric.value)}%`;

export const formatMoney = (metric: MoneyMetricDto): string =>
  metric.availability === 'unavailable'
    ? UNAVAILABLE
    : new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: metric.currency,
      }).format(metric.cents / 100);

/**
 * A legenda de uma medida COM origem.
 *
 * Ela existe porque o traço sozinho não diz nada: "Indisp." embaixo de "últimos
 * 30 dias" pode ser coleta parada, recusa da Meta ou medição de outro tamanho,
 * e a ação de quem lê é diferente em cada caso. Quando existe medição de outra
 * janela, a legenda diz QUAL janela existe, que é a resposta honesta que o
 * plano pede em vez de somar trinta alcances diários.
 *
 * Nenhum texto daqui vem do servidor: o servidor manda motivo fechado e
 * números, e a frase é montada aqui. Motivo que chega como texto pronto é texto
 * de fora entrando na tela.
 */
export const describeSourcedMetric = (
  metric: SourcedMetricDto,
  quandoDisponivel: string,
): string => {
  if (metric.availability === 'available') {
    return quandoDisponivel;
  }
  const amostra = metric.sample;
  switch (metric.gap) {
    case 'never_collected':
      return 'a coleta não rodou';
    case 'stale':
      return metric.instead
        ? `última medição em ${formatShortDate(metric.instead.measuredAt)}`
        : 'medição vencida';
    case 'window_mismatch':
      return metric.instead
        ? `só ${formatDays(metric.instead.measuredWindowDays)} medido`
        : 'janela medida é outra';
    case 'meta_unavailable':
      return 'a Meta não devolveu';
    case 'incomplete_sample':
      return amostra
        ? `${amostra.measured} de ${amostra.eligible} peças medidas`
        : 'medição parcial';
    case 'insufficient_sample':
      return amostra
        ? `amostra de ${amostra.measured}, mínimo 2`
        : 'amostra pequena demais';
  }
};

/** `1` vira `1 dia`; `30` vira `30 dias`. Fração some: janela é dia inteiro. */
export const formatDays = (days: number): string => {
  const inteiro = Math.round(days);
  return inteiro === 1 ? '1 dia' : `${PLAIN.format(inteiro)} dias`;
};

const SHORT_DATE = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  timeZone: 'America/Sao_Paulo',
});

/** Data curta em horário de Brasília, que é o fuso da operação. */
export const formatShortDate = (iso: string): string =>
  SHORT_DATE.format(new Date(iso));

/** `{measured, eligible}` vira `3 de 8`. */
export const formatSample = (
  sample: {measured: number; eligible: number} | null,
): string =>
  sample ? `${sample.measured} de ${sample.eligible}` : 'sem amostra';

export const HEALTH_LABEL: Record<HealthStatus, string> = {
  healthy: 'SAUDÁVEL',
  degraded: 'DEGRADADO',
  failed: 'FALHOU',
};

export const HEALTH_CLASS: Record<HealthStatus, string> = {
  healthy: 'dm-ok',
  degraded: 'dm-ok is-degraded',
  failed: 'dm-ok is-failed',
};

/** `ATIVO` para o nível em uso, `30 DIAS` para o que ainda espera. */
export const autonomyEligibilityLabel = (
  eligibility: AutonomyLevelDto['eligibility'],
): string =>
  eligibility.kind === 'active' ? 'ATIVO' : `${eligibility.days} DIAS`;
